import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logo from "../../traderhub24-logo.svg";
import FeedbackForm from "./FeedbackForm";
import { useAuth } from '../contexts/AuthContext';
import { useDashboardSidebar } from '../contexts/DashboardSidebarContext';

interface FooterProps {
  currentPage?: string;
  compact?: boolean;
  onNavigate?: (page: "home" | "pricing" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void;
}

function Footer({ currentPage, compact = false, onNavigate }: FooterProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  
  const handleNavigation = (page: "home" | "pricing" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => {
    window.scrollTo(0, 0);
    if (onNavigate) {
      onNavigate(page);
    } else {
      switch (page) {
        case "home": navigate("/"); break;
        case "pricing": navigate("/pricing"); break;
        case "changelog": navigate("/changelog"); break;
        case "privacy-policy": navigate("/privacy"); break;
        case "terms-and-conditions": navigate("/terms"); break;
        case "risk-warning": navigate("/risk-warning"); break;
        case "documentation": navigate("/documentation"); break;
        case "contact": navigate("/contact"); break;
        default: break;
      }
    }
  };

  const link = (page: "pricing" | "changelog" | "documentation" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "contact", labelKey: string) =>
    currentPage === page ? (
      <span className="text-sm text-orange-500 font-medium">{t(labelKey)}</span>
    ) : (
      <a
        href={page === "pricing" ? "/pricing" : page === "changelog" ? "/changelog" : page === "documentation" ? "/documentation" : page === "privacy-policy" ? "/privacy" : page === "terms-and-conditions" ? "/terms" : page === "risk-warning" ? "/risk-warning" : "/contact"}
        onClick={(e) => { e.preventDefault(); handleNavigation(page); }}
        className="text-sm text-gray-600 hover:text-orange-500 transition-colors"
      >
        {t(labelKey)}
      </a>
    );

  // Compact mode: fixed at bottom, expand upward overlaying content, never over sidebar
  const sidebar = useDashboardSidebar();
  const avoidSidebar = sidebar && !sidebar.sidebarCollapsed ? 'md:left-64' : '';

  if (compact) {
    return (
      <footer className={`fixed bottom-0 left-0 right-0 z-40 bg-gray-50 border-t border-gray-200 flex-shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] ${avoidSidebar}`}>
        {/* Expanded content - above the bar, overlays dashboard */}
        <div
          className={`absolute bottom-full left-0 right-0 bg-gray-50 border-t border-gray-200 overflow-hidden transition-all duration-200 ease-in-out shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] ${
            expanded ? 'max-h-[280px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
          }`}
        >
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wide">{t('footer.links')}</h3>
                <ul className="space-y-1.5 text-sm">
                  <li>{link("pricing", "footer.pricing")}</li>
                  <li>{link("changelog", "footer.changelog")}</li>
                  <li>{link("documentation", "footer.documentation")}</li>
                  <li><a href="/blog" className="text-gray-600 hover:text-orange-500">{t('footer.blog')}</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wide">{t('footer.legal')}</h3>
                <ul className="space-y-1.5 text-sm">
                  <li>{link("privacy-policy", "footer.privacyPolicy")}</li>
                  <li>{link("terms-and-conditions", "footer.termsOfService")}</li>
                  <li>{link("risk-warning", "footer.riskWarning")}</li>
                  <li>{link("contact", "footer.contact")}</li>
                  {user && (
                    <li>
                      <button onClick={() => setShowFeedbackForm(true)} className="text-gray-600 hover:text-orange-500">
                        {t('footer.feedback')}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Single line bar - always visible */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={logo} alt="TraderHub24" className="h-8 w-auto flex-shrink-0" />
            <span className="text-xs text-gray-500 truncate">
              © {new Date().getFullYear()} TraderHub24. {t('footer.allRightsReserved')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={expanded ? t('footer.collapse') || 'Collapse' : t('footer.expand') || 'Expand'}
            aria-expanded={expanded}
          >
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
        <FeedbackForm isOpen={showFeedbackForm} onClose={() => setShowFeedbackForm(false)} />
      </footer>
    );
  }

  // Full footer for non-dashboard pages
  return (
    <footer className="bg-gray-50 border-t border-gray-200 mt-16">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img src={logo} alt="TraderHub24" className="h-12 w-auto" />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{t('footer.brandDescription')}</p>
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} TraderHub24. {t('footer.allRightsReserved')}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('footer.links')}</h3>
            <ul className="space-y-2">
              <li>{link("pricing", "footer.pricing")}</li>
              <li><span className="text-sm text-gray-600">{t('footer.tools')}</span></li>
              <li>{link("changelog", "footer.changelog")}</li>
              <li>{link("documentation", "footer.documentation")}</li>
              <li><a href="/blog" className="text-sm text-gray-600 hover:text-orange-500">{t('footer.blog')}</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>{link("privacy-policy", "footer.privacyPolicy")}</li>
              <li>{link("terms-and-conditions", "footer.termsOfService")}</li>
              <li>{link("risk-warning", "footer.riskWarning")}</li>
              <li>{link("contact", "footer.contact")}</li>
              {user && (
                <li>
                  <button onClick={() => setShowFeedbackForm(true)} className="text-sm text-gray-600 hover:text-orange-500">{t('footer.feedback')}</button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <FeedbackForm isOpen={showFeedbackForm} onClose={() => setShowFeedbackForm(false)} />
    </footer>
  );
}

export default Footer;
